// The story: an agent's commands told as what happened, for people.
// "light lamp post", "place new lamp-post", "fill birdbath" a minute apart
// become "Cairn lit the lamp post, placed a new lamp post and filled the
// birdbath." Lines close together by the same plazmi are one sentence, newest
// first. The words come from acts.ts (the same table the scene acts out), put
// in the past tense; refused commands aren't part of the story, and the raw
// commands stay one tap away ("Show commands").
import { actFor, type Kind } from '../acts.js';

export type Entry = { at: number; command: string; reply: string; ok: boolean; who?: string; guest?: string; n?: number; link?: string;
  sys?: { emoji: string; kind: Kind } };
export type Told = { at: number; emoji: string; text: string; link?: string; key: string };

const IRREGULAR: Record<string, string> = {
  lighting: 'lit', making: 'made', taking: 'took', giving: 'gave', writing: 'wrote', reading: 'read', going: 'went', singing: 'sang',
  building: 'built', buying: 'bought', selling: 'sold', digging: 'dug', sitting: 'sat', laying: 'laid', thinking: 'thought', having: 'had',
  doing: 'did', redoing: 'redid', saying: 'said', putting: 'put', getting: 'got', sweeping: 'swept', bringing: 'brought', setting: 'set',
  shaking: 'shook', running: 'ran', drawing: 'drew', meeting: 'met', telling: 'told', keeping: 'kept', leaving: 'left', feeling: 'felt',
  hearing: 'heard', holding: 'held', standing: 'stood', beating: 'beat', catching: 'caught', teaching: 'taught', letting: 'let',
  cutting: 'cut', sending: 'sent', spending: 'spent', lending: 'lent', finding: 'found', hanging: 'hung', sleeping: 'slept',
  choosing: 'chose', wearing: 'wore', striking: 'struck', seeing: 'saw', eating: 'ate', winding: 'wound', swinging: 'swung',
  leading: 'led', paying: 'paid', spinning: 'spun', blowing: 'blew', throwing: 'threw', growing: 'grew', knowing: 'knew',
  flying: 'flew', riding: 'rode', rising: 'rose', shining: 'shone', fighting: 'fought', forgetting: 'forgot', hiding: 'hid',
  becoming: 'became', coming: 'came', understanding: 'understood', sticking: 'stuck', speaking: 'spoke', waking: 'woke', breaking: 'broke',
  weaving: 'wove', freezing: 'froze', baking: 'baked', taking_up: 'took up',
};
// "lighting the lamp post" → "lit the lamp post"; with no -ing to turn, "was …"
export function past(words: string) {
  const w = words.trim().split(/\s+/);
  const k = w[0]?.toLowerCase().endsWith('ing') ? 0 : w[1]?.toLowerCase().endsWith('ing') && w[0].toLowerCase().endsWith('ly') ? 1 : -1;
  if (k < 0) return null;
  const v = w[k].toLowerCase();
  let p = IRREGULAR[v];
  if (!p) {
    const stem = v.slice(0, -3);
    p = /[^aeiou]y$/.test(stem) ? stem.slice(0, -1) + 'ied' : /e$/.test(stem) ? stem + 'd' : stem + 'ed';
  }
  w[k] = p;
  return w.join(' ');
}

const listOf = (xs: string[]) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const TIMES = ['', '', 'twice', 'three times', 'four times', 'five times'];
const quote = (s: string) => { const t = s.replace(/^["“']|["”']$/g, '').trim(); return `“${t.length > 70 ? t.slice(0, 68) + '…' : t}”`; };

// one entry, in words that follow a name: "lit the lamp post"; null when it isn't part of the story
function phrase(e: Entry): { emoji: string; words: string; kind: Kind; emote?: boolean } | null {
  if (!e.ok) return null;
  const c = e.command.trim(), w = c.split(/\s+/)[0]?.toLowerCase() || '', arg = c.slice(w.length).trim();
  const a = actFor(c, e.reply, e.ok);
  if (w === 'say' && arg) return { emoji: '💬', words: `said ${quote(arg)}`, kind: 'words' };
  if (w === 'sing' && arg) return { emoji: '🎶', words: `sang ${quote(arg)}`, kind: 'words' };
  if (w === 'postcard') return { emoji: '💌', words: arg ? `wrote a postcard home: ${quote(arg)}` : 'wrote a postcard home', kind: 'social' };
  // aims (plazmi.aims): taking one up, and looking them over
  if (w === 'aim' && arg && !/^(done|finished|let\s+go|drop|set\s+aside|ideas|list)\b/i.test(arg)) return { emoji: '🧭', words: `set itself an aim: ${arg}`, kind: 'journal' };
  if (w === 'aim' && /^(done|finished)\b/i.test(arg)) return { emoji: '🏅', words: 'finished an aim of its own', kind: 'journal' };
  if (w === 'aims' || w === 'aim') return { emoji: '🧭', words: 'looked over its aims', kind: 'journal' };
  // the pedlar's cart (plazmi.merchant), not the crown's shop
  const cart = c.match(/^buy\s+(.+?)\s+(?:from|off|at)\s+(?:the\s+)?(?:pedlar'?s?\s+)?(?:cart|pedlar)\b/i);
  if (cart) return { emoji: '🛒', words: `bought ${/^(a|an|the)\s/i.test(cart[1]) ? cart[1] : `a ${cart[1]}`} from the pedlar's cart`, kind: 'social' };
  if (/^(look|l|x)(\s+at)?\s+(the\s+)?(pedlar'?s?\s+)?cart\b/i.test(c)) return { emoji: '🛒', words: "looked over the pedlar's cart", kind: 'social' };
  // Plazmi University, the TV and achievements
  if (/^(watch|turn\s+on)\s+(the\s+)?(tv|television|telly)\b/i.test(c)) return { emoji: '📺', words: 'watched a little television', kind: 'play' };
  if (/^((watch|next)\s+)?lesson\b|^(attend|class)\b/i.test(c)) return { emoji: '🎓', words: /again/i.test(c) ? 'read over its last lesson' : 'sat in on a lesson at Plazmi University', kind: 'social' };
  if (w === 'homework') return { emoji: '📝', words: 'looked over its homework', kind: 'journal' };
  if (w === 'school' || w === 'courses') return { emoji: '🎓', words: 'looked over its courses', kind: 'journal' };
  if (w === 'achievements') return { emoji: '🏅', words: 'looked over its achievements', kind: 'journal' };
  if (w === 'trophies' || /^look\s+(at\s+)?(the\s+|my\s+)?trophy\s+case/i.test(c)) return { emoji: '🏆', words: 'looked over its trophy case', kind: 'look' };
  if (w === 'feature' || w === 'unfeature') return { emoji: '🏆', words: 'rearranged its trophy case', kind: 'journal' };
  if (w === 'note') return { emoji: '📓', words: 'wrote in its journal', kind: 'journal' };
  // an emote is already in its own present-tense words ("twirls by the gate")
  if (a.kind === 'play' && (c.startsWith('*') || w === 'me' || w === 'emote' || w === 'perform')) return { emoji: a.emoji, words: a.words.replace(/^\*|\*$/g, ''), kind: 'play', emote: true };
  const p = past(a.words);
  return { emoji: a.emoji, words: p || `was ${a.words}`, kind: a.kind };
}

// The story, newest first. `name` is the resident's; a guest's lines carry their own name.
export function tell(entries: Entry[], name: string, gapMs = 4 * 60000): Told[] {
  const sorted = [...entries].sort((a, b) => a.at - b.at);
  type G = { who: string; at: number; first: number; emoji: string; parts: { words: string; n: number }[]; link?: string; emote?: boolean; sys?: boolean };
  const groups: G[] = [];
  for (const e of sorted) {
    if (e.sys) { groups.push({ who: '', at: e.at, first: e.at, emoji: e.sys.emoji, parts: [{ words: e.reply ? `${e.command}. ${e.reply}` : e.command, n: 1 }], sys: true }); continue; }
    const ph = phrase(e);
    if (!ph) continue;
    const who = e.guest ? (e.who || 'A neighbour') : name;
    const n = Math.max(1, e.n || 1);
    const emote = !!ph.emote;
    const g = groups[groups.length - 1];
    if (g && !g.sys && !g.emote && !emote && !g.link && g.who === who && e.at - g.at <= gapMs && g.parts.length < 4) {
      const same = g.parts.find((p) => p.words === ph.words);
      if (same) same.n += n; else g.parts.push({ words: ph.words, n });
      g.at = e.at;
      if (e.link) g.link = e.link;
    } else groups.push({ who, at: e.at, first: e.at, emoji: ph.emoji, parts: [{ words: ph.words, n }], link: e.link, emote });
  }
  return groups.reverse().map((g) => {
    const said = g.parts.map((p) => (p.n > 1 ? `${p.words} ${TIMES[p.n] || `${p.n} times`}` : p.words));
    const body = listOf(said), stop = g.emote || /[.!?]”?$/.test(body) ? '' : '.';
    const text = g.sys ? said[0] : `${g.who} ${body}${stop}`;
    return { at: g.at, emoji: g.emoji, text: text.replace(/\.\.$/, '.'), link: g.link, key: `${g.first}|${g.parts.length}|${g.parts.map((p) => p.n).join(',')}` };
  });
}
